import { useCallback, useEffect, useRef, useState } from 'react';
import { AText, Box, Button, Overlay } from '@alf/uikit';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import { useI18n } from '@namespace/i18n';
import { Icon } from '@namespace/icons2';
import { useNotifications } from '@namespace/notifications';
import { useHistory } from '@namespace/router';
import { useBettingCurrency, USER_ROLES } from '@namespace/user';
import { checkSpinWheel } from '../../api/checkSpinWheel';
import { postSpinWheel } from '../../api/postSpinWheel';
import { useWheelStore, wheelActions } from '../../store';
import { CustomCSSProperties, WheelSegment, WheelSettings } from '../../types';
import { confettiBlast } from '../../utils/confettiBlast';
import { CountdownButton } from '../CountdownButton';
import { Wheel } from '../Wheel';
import { segmentsTypes } from '../Wheel/config';
import { NoPrize } from './components/NoPrize';
import { modalTitles } from './config';

import styles from './index.module.css';

const { SET_WHEEL_STATE, SET_WINNER_SEGMENT, SET_SPIN_TIME } = wheelActions;

type WheelModalProps = {
  userRole: (typeof USER_ROLES)[keyof typeof USER_ROLES];
  userInfoLoaded: boolean;
};

export const WheelModal = ({ userRole, userInfoLoaded }: WheelModalProps) => {
  const { t } = useI18n();
  const { push } = useHistory();
  const { casinoBonusList } = useGeneralRoutes();
  const {
    wheelOfFortune = { enabled: false, settings: [] }
  }: { wheelOfFortune: WheelSettings } = useOtherSettings();

  const { settings } = wheelOfFortune;

  const { successNotification, errorNotification } = useNotifications();
  const currency = useBettingCurrency();

  const { isMobile, isTablet } = useDeviceStore();
  const { spinTime } = useWheelStore();

  const wheelRef = useRef<{
    spinWheel: () => void;
    stopAndSpinToWinningSegment: (id: number | string) => void;
    emergencyStop: () => void;
    height: number | undefined;
  }>(null);

  const [spinDisabled, setSpinDisabled] = useState(true);
  const [winData, setWinData] = useState<WheelSegment | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(300);

  const checkUserCanSpin = useCallback(async () => {
    const { data, status } = await checkSpinWheel(currency);

    if (status === 'success') {
      setSpinDisabled(false);
      SET_SPIN_TIME(null);
    }

    if (data?.spinAt) {
      SET_SPIN_TIME(data?.spinAt);
    }
  }, [currency]);

  const onFinishCountdown = useCallback(() => {
    checkUserCanSpin();
  }, [checkUserCanSpin]);

  const onCloseModal = () => {
    SET_WHEEL_STATE(false);
    setWinData(null);
  };

  const onFinish = (winSegment: WheelSegment) => {
    setContentHeight(wheelRef.current?.height || 300);

    setInterval(() => {
      setWinData(winSegment);
    }, 1000);

    if (winSegment.type !== segmentsTypes.empty) {
      confettiBlast();
    }
  };

  const onSpin = async () => {
    wheelRef.current?.spinWheel();

    const res = await postSpinWheel(currency);

    if (res && 'prizeId' in res) {
      const { prizeId } = res;
      SET_WINNER_SEGMENT(prizeId);
      wheelRef.current?.stopAndSpinToWinningSegment(prizeId);
      setSpinDisabled(true);
    } else {
      errorNotification(t('web.wheel.spin.error'));
      wheelRef.current?.emergencyStop();
    }
  };

  const onReceive = () => {
    onCloseModal();
    push(casinoBonusList);
    successNotification(t('web.wheel.bonus.success'));
  };

  useEffect(() => {
    if (userInfoLoaded && userRole === USER_ROLES.USER) {
      checkUserCanSpin();
    }
  }, [userInfoLoaded, userRole, winData, checkUserCanSpin]);

  const renderTitle = useCallback(() => {
    let title = modalTitles.idle;

    if (winData) {
      if (winData.type === segmentsTypes.empty) {
        title = modalTitles.lose;
      } else {
        title = modalTitles.win;
      }
    }

    return (
      <AText
        size="3xl"
        transform="uppercase"
        color="po_txt_color_1"
        className={styles.modalTitle}
      >
        {t(title)}
      </AText>
    );
  }, [winData]);

  const renderContent = () => {
    if (!winData) {
      return (
        <Wheel
          segments={settings}
          onFinished={onFinish}
          ref={wheelRef}
          setDisabled={setSpinDisabled}
        />
      );
    }

    if (winData.type === segmentsTypes.empty) {
      return <NoPrize height={contentHeight || 300} />;
    }

    return (
      <Box
        direction="column"
        justify="center"
        align="center"
        className={styles.reward}
        style={
          {
            '--height': `${contentHeight || 300}px`
          } as CustomCSSProperties
        }
      >
        <AText
          breed="RU"
          size="3xl"
          transform="uppercase"
          color="po_txt_color_1"
          className={styles.rewardTitle}
        >
          {t('web.wheel.win.content')}
        </AText>
        <AText breed="BU" component="strong" className={styles.rewardContent}>
          {winData.type === segmentsTypes.ads
            ? `${winData.value} free spins`
            : `${winData.value}% to deposit`}
        </AText>
      </Box>
    );
  };

  const renderButton = () => {
    if (winData) {
      if (winData.type === segmentsTypes.empty) {
        return (
          <CountdownButton
            spinTime={spinTime}
            onCountdownFinish={onFinishCountdown}
            className={styles.spinButton}
          />
        );
      }

      return (
        <Button
          onClick={onReceive}
          dataRole="wheel-collect-button"
          className={styles.spinButton}
        >
          {t('web.wheel.win.button')}
        </Button>
      );
    }

    if (spinTime) {
      return (
        <CountdownButton
          spinTime={spinTime}
          onCountdownFinish={onFinishCountdown}
          className={styles.spinButton}
        />
      );
    }

    return (
      <Button
        onClick={!spinDisabled ? onSpin : undefined}
        disabled={spinDisabled}
        dataRole="spin-wheel-button"
        className={styles.spinButton}
      >
        {t('web.wheel.spin.button')}
      </Button>
    );
  };

  return (
    <Overlay
      classNames={{ wrapperClass: styles.overlay }}
      // @ts-expect-error TODO: fix the type of onClick
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      <Box className={styles.wheelPopup} direction="column" align="center">
        {winData && winData.type !== segmentsTypes.empty ? (
          <Box className={styles.glitterBg} />
        ) : null}

        <Box className={styles.header} justify="center" align="center">
          {renderTitle()}
          <Icon
            onClick={onCloseModal}
            name="icons/general/action/close"
            size={isMobile || isTablet ? 's' : 'xl'}
            color="po_txt_color_1"
            className={styles.close}
          />
        </Box>

        <Box className={styles.wheelContainer}>{renderContent()}</Box>
        <Box className={styles.buttonContainer}>{renderButton()}</Box>
      </Box>
    </Overlay>
  );
};
